import {useRef} from "react";
import {requestApi} from "@/boxhub-api";
import {transformToErrorShape} from "@/lib/accessor";

type DateFieldValue = string | Date | null | undefined;

interface DateValues {
    validUntil?: DateFieldValue;
    deliveryDateFrom?: DateFieldValue;
    deliveryDateTo?: DateFieldValue;
}

/**
 * Returns a memoized async validator function that calls the backend date validation API.
 * Results are cached by the combination of the three date values to avoid redundant API calls.
 *
 * @param disabled - When true, the validator always returns an empty error object (e.g. in readonly mode).
 * @returns An async validator function that accepts a DateValues object and returns a validation error shape.
 */
export function useRequestDatesValidator(disabled: boolean): (dates: DateValues) => Promise<Record<string, unknown>> {
    const errorRef = useRef({ key: "", error: {} as Record<string, unknown> });

    return async (dates: DateValues): Promise<Record<string, unknown>> => {
        if (disabled) return {};

        const newKey = `${dates.deliveryDateFrom}-${dates.deliveryDateTo}-${dates.validUntil}`;
        if (errorRef.current.key !== newKey) {
            errorRef.current.key = newKey;
            const response = await requestApi.validateRequestDates({
                deliveryDateFrom: dates.deliveryDateFrom as string,
                deliveryDateTo: dates.deliveryDateTo as string,
                validUntil: dates.validUntil as string
            });
            errorRef.current.error = transformToErrorShape(response.data?.error?.errors) as Record<string, unknown>;
        }
        return errorRef.current.error;
    };
}
